const millisecondsInADay = 86_400_000;

export const daysSince = (sinceDate?: string): number => {
  if (!sinceDate) {
    return 1;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const waitingSince = new Date(sinceDate);
  waitingSince.setHours(0, 0, 0, 0);
  return Math.round(
    (today.getTime() - waitingSince.getTime()) / millisecondsInADay,
  );
};

export const toIsoString = (date?: string | Date): string | undefined => {
  if (!date) {
    return undefined;
  }

  const parsed = date instanceof Date ? date : new Date(date);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
};

/**
 * Converts a date to an ISO date string.
 * @param date The Date object to convert.
 * @returns A date string in the following format: yyyy-mm-dd.
 */
export const toIsoDateString = (date?: string | Date): string | undefined => {
  if (!date) {
    return;
  }

  const isoString = toIsoString(date);
  if (!isoString) {
    return;
  }

  return isoString.split('T')[0];
};

/**
 * Converts a date to a 2-digit month and year.
 * @param date The Date object to convert.
 * @returns A date string in the following format: mm/yy.
 */
export const toTwoDigitDateString = (date?: Date): string | undefined => {
  if (!date) {
    return;
  }

  const options: Intl.DateTimeFormatOptions = {
    month: '2-digit',
    year: '2-digit',
  };

  return new Intl.DateTimeFormat('en-US', options).format(date);
};

/** Generate select items for the 12 calendar months. */
export const generateMonthItems = () =>
  Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    return {
      value: month.toString(),
      // TODO: locale-aware month names
      text: Intl.DateTimeFormat('en', { month: 'long' }).format(
        new Date(2000, i),
      ),
    };
  });

/** Generate select items for days 1–31. */
export const generateDayItems = () =>
  Array.from({ length: 31 }, (_, i) => {
    const day = i + 1;
    return {
      value: day.toString(),
      text: day.toString(),
    };
  });

/**
 * Formats a date string or Date object into a short human-readable format.
 * @param date - ISO date string or Date object
 * @returns Formatted string (e.g. "Jun 2, 2026") or undefined if invalid
 */
export const toShortDateString = (
  date?: string | Date | null,
): string | undefined => {
  if (!date) {
    return undefined;
  }

  const parsed = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Formats a date string or Date object into a long human-readable format with time.
 * @param date - ISO date string or Date object
 * @returns Formatted string (e.g. "June 2, 2026, 5:34 PM") or undefined if invalid
 */
export const toLongDate12hrTimeString = (
  date?: string | Date | null,
): string | undefined => {
  if (!date) {
    return undefined;
  }

  const parsed = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(parsed);
};
